// SmartInputBar.jsx -- hub-myhub-v8.jsx InputBar port
// ASCII only. var declarations. ESM only. Inline styles.
import { useContext, useState, useRef, useEffect } from 'react';
import { ThemeContext, MONO } from '../lib/theme.js';
import { AGENT_ICON_SVG } from '../lib/agent-icons.js';

var purple = '#8B5CF6';

export default function SmartInputBar({ agent, hasPill, onPill, onNewThread, onSearch, value, onChange, onSend, onStop, sending, attachments, onRemoveAttachment, onFileSelect, onDragOver, onDrop, onPaste, offRecord, onOffRecordChange, hideOffRecord, inputRef, fileInputRef, onMicToggle, dictating }) {
  var theme = useContext(ThemeContext);

  // Voice input via Web Speech API
  var _listening = useState(false);
  var listening = _listening[0];
  var setListening = _listening[1];
  var recognitionRef = useRef(null);

  var _voiceMemoMode = useState(false);
  var voiceMemoMode = _voiceMemoMode[0];
  var setVoiceMemoMode = _voiceMemoMode[1];
  var _memoChunks = useState([]);
  var memoChunks = _memoChunks[0];
  var setMemoChunks = _memoChunks[1];
  var _memoRecorder = useState(null);
  var memoRecorder = _memoRecorder[0];
  var setMemoRecorder = _memoRecorder[1];
  var longPressTimerRef = useRef(null);
  var _textareaRef = useRef(null);
  var _memoTranscript = useState('');
  var memoTranscript = _memoTranscript[0];
  var setMemoTranscript = _memoTranscript[1];
  var _showMemoTeach = useState(false);
  var showMemoTeach = _showMemoTeach[0];
  var setShowMemoTeach = _showMemoTeach[1];

  useEffect(function() {
    return function() {
      if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch(e) {} }
    };
  }, []);

  useEffect(function() {
    var el = (inputRef && inputRef.current) || _textareaRef.current;
    if (!el) return;
    if (!value) {
      el.style.height = 'auto';
    } else {
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 120) + 'px';
    }
  }, [value]);

  function handleMicDown() {
    if (sending) return;
    // If already in voice memo mode, tap stops the memo
    if (voiceMemoMode) {
      stopVoiceMemo();
      return;
    }
    // If already listening (speech-to-text), tap stops it
    if (listening) {
      if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch(e) {} }
      setListening(false);
      return;
    }
    // Start long-press timer -- 500ms = voice memo
    longPressTimerRef.current = setTimeout(function() {
      longPressTimerRef.current = null;
      startVoiceMemo();
    }, 500);
  }

  function handleMicUp() {
    // If long-press timer still running, it was a short tap -- do speech-to-text
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
      toggleMic();
    }
    // If in voice memo mode, don't stop on mouseUp -- user taps again to stop
  }

  function toggleMic() {
    var SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Speech recognition not supported in this browser. Use Chrome.');
      return;
    }
    var recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = navigator.language || 'en-US';
    var finalTranscript = value || '';
    recognition.onresult = function(event) {
      var interim = '';
      for (var i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          finalTranscript += (finalTranscript ? ' ' : '') + event.results[i][0].transcript;
        } else {
          interim += event.results[i][0].transcript;
        }
      }
      if (onChange) onChange(finalTranscript + (interim ? ' ' + interim : ''));
    };
    recognition.onerror = function() { setListening(false); };
    recognition.onend = function() { setListening(false); recognitionRef.current = null; };
    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  }

  function startVoiceMemo() {
    // First-use teach
    try {
      var hasSeenTeach = localStorage.getItem('hub_voice_memo_taught');
      if (!hasSeenTeach) {
        setShowMemoTeach(true);
        localStorage.setItem('hub_voice_memo_taught', '1');
      }
    } catch(e) {}

    setVoiceMemoMode(true);
    setMemoTranscript('');

    // Start MediaRecorder for audio capture
    navigator.mediaDevices.getUserMedia({ audio: true }).then(function(stream) {
      var recorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      var chunks = [];
      recorder.ondataavailable = function(e) { if (e.data.size > 0) chunks.push(e.data); };
      recorder.onstop = function() {
        stream.getTracks().forEach(function(t) { t.stop(); });
        var blob = new Blob(chunks, { type: 'audio/webm' });
        submitVoiceMemo(blob);
      };
      recorder.start(1000);
      setMemoRecorder(recorder);
      setMemoChunks(chunks);

      // Also run SpeechRecognition for live preview
      var SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        var rec = new SpeechRecognition();
        rec.continuous = true;
        rec.interimResults = true;
        rec.lang = navigator.language || 'en-US';
        rec.onresult = function(event) {
          var text = '';
          for (var i = 0; i < event.results.length; i++) {
            text += event.results[i][0].transcript + ' ';
          }
          setMemoTranscript(text.trim());
        };
        rec.onend = function() {};
        rec.onerror = function() {};
        recognitionRef.current = rec;
        rec.start();
      }
    }).catch(function(e) {
      setVoiceMemoMode(false);
      alert('Microphone access denied. Allow mic permission and try again.');
    });
  }

  function stopVoiceMemo() {
    if (memoRecorder && memoRecorder.state !== 'inactive') {
      memoRecorder.stop();
    }
    if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch(e) {} }
    setVoiceMemoMode(false);
    setMemoRecorder(null);
  }

  function submitVoiceMemo(blob) {
    // Convert to base64 and send to voice memo endpoint
    var reader = new FileReader();
    reader.onloadend = function() {
      var base64 = reader.result.split(',')[1];
      fetch('/api/voice-memo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          audio: base64,
          transcript_preview: memoTranscript,
          duration_seconds: Math.round(blob.size / 16000) // rough estimate
        })
      }).then(function(r) { return r.json(); }).then(function(d) {
        if (d.ok) {
          // Show confirmation briefly
          if (onChange) onChange('');
        }
      }).catch(function() {});
    };
    reader.readAsDataURL(blob);
    setMemoTranscript('');
  }

  var agentColor = (agent && agent.color) ? agent.color : theme.accent;
  var agentAbbr  = (agent && agent.abbr)  ? agent.abbr  : 'H';
  var agentName  = (agent && agent.name)  ? agent.name  : 'Hub';

  return (
    <div style={{ flexShrink: 0, padding: '0 12px', maxWidth: 620, margin: '0 auto', width: '100%' }}>
      {/* Control row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>

        {/* New thread -- stage 2+, first position */}
        {(
          <div data-testid="new-thread-btn" onClick={onNewThread || function(){}} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 14, background: theme.surfaceInset, cursor: 'pointer', border: 'none', whiteSpace: 'nowrap', fontWeight: 500 }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={theme.textSecondary} strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
            <span style={{ fontSize: 11, color: theme.textSecondary }}>New thread</span>
          </div>
        )}

        {/* Learning / Off-record toggle -- stage 2+, second position */}
        {!hideOffRecord && (
          <div onClick={function() { if (onOffRecordChange) onOffRecordChange(!offRecord); }} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 14, background: offRecord ? purple + '14' : theme.surfaceInset, cursor: 'pointer', border: 'none', whiteSpace: 'nowrap', fontWeight: 500 }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={offRecord ? purple : theme.textSecondary} strokeWidth="2">
              {offRecord
                ? <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19M1 1l22 22"/>
                : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>
              }
            </svg>
            <span style={{ fontSize: 11, color: offRecord ? purple : theme.textSecondary }}>{offRecord ? 'Off record' : 'Learning'}</span>
          </div>
        )}

        <div style={{ flex: 1 }} />

        {/* cmd+K search pill -- right-justified */}
        {onSearch && !hasPill && (
          <div onClick={onSearch} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: 14, cursor: 'pointer', background: theme.surfaceInset, whiteSpace: 'nowrap' }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={theme.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <span style={{ fontSize: 9, fontFamily: "'IBM Plex Mono', monospace", color: theme.textMuted }}>cmd+K</span>
          </div>
        )}

        {/* Agent pill with cmd+K hint -- far right, only when hasPill */}
        {hasPill && (
          <div onClick={onPill} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 14, background: theme.accentLight || (agentColor + '14'), cursor: 'pointer', border: 'none', whiteSpace: 'nowrap', fontWeight: 500 }}>
            <div style={{ width: 18, height: 18, borderRadius: '50%', background: agentColor + '25', color: agentColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 7, fontWeight: 700, fontFamily: MONO }}>{agent && agent.hubId && AGENT_ICON_SVG[agent.hubId] ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: AGENT_ICON_SVG[agent.hubId] }} /> : agentAbbr}</div>
            <span style={{ fontSize: 11, fontWeight: 500, color: agentColor }}>{agentName}</span>
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke={agentColor} strokeWidth="1.5"><polyline points="3,4 6,7 9,4"/></svg>
            <span style={{ fontFamily: MONO, fontSize: 9, marginLeft: 4, opacity: 0.7, color: agentColor }}>cmd+K</span>
          </div>
        )}
      </div>

      {/* Hidden file input */}
      <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={onFileSelect || function() {}} />

      {/* Attachment pills */}
      {attachments && attachments.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 4 }}>
          {attachments.map(function(att, idx) {
            return (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '2px 6px', borderRadius: 6, background: theme.accent + '14', border: '0.5px solid ' + theme.accent + '30', fontSize: 9 }}>
                <span style={{ color: theme.textSecondary }}>{att.type === 'image' ? 'IMG' : 'PDF'}</span>
                <span style={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: theme.textSecondary }}>{att.name}</span>
                <span onClick={function() { if (onRemoveAttachment) onRemoveAttachment(idx); }} style={{ cursor: 'pointer', color: theme.textMuted, fontSize: 10, lineHeight: 1 }}>x</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Input container -- unified box with mic/clip/send inside */}
      <div onDragOver={onDragOver || function(e) { e.preventDefault(); }} onDrop={onDrop || function() {}} style={{
        background: theme.surfaceInset, borderRadius: 14, border: '1px solid ' + theme.border,
        padding: '8px 10px', display: 'flex', alignItems: 'center', gap: 6, minHeight: 44, position: 'relative'
      }}>
        {/* Mic */}
        <div data-testid="mic-button" onMouseDown={handleMicDown} onMouseUp={handleMicUp} onMouseLeave={function() { if (longPressTimerRef.current) { clearTimeout(longPressTimerRef.current); longPressTimerRef.current = null; } }} onTouchStart={handleMicDown} onTouchEnd={handleMicUp} style={{ width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer', background: voiceMemoMode ? '#7C3AED20' : listening ? (theme.critical || '#A32D2D') + '20' : 'transparent', transition: 'background 0.2s' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={voiceMemoMode ? '#7C3AED' : listening ? (theme.critical || '#A32D2D') : theme.textMuted} strokeWidth="2" style={{ transition: 'all 0.2s' }}>
            <rect x="9" y="2" width="6" height="11" rx="3"/>
            <path d="M5 10a7 7 0 0014 0M12 17v4M8 21h8"/>
          </svg>
        </div>

        {/* Voice memo recording indicator */}
        {voiceMemoMode && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#7C3AED', animation: 'pulse 1.5s infinite' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: '#7C3AED' }}>Recording voice memo...</div>
              {memoTranscript && <div style={{ fontSize: 9, color: theme.textSecondary, marginTop: 2, maxHeight: 40, overflow: 'hidden' }}>{memoTranscript}</div>}
            </div>
            <div onClick={stopVoiceMemo} style={{ fontSize: 9, fontWeight: 600, color: '#7C3AED', cursor: 'pointer', padding: '4px 10px', borderRadius: 8, background: '#7C3AED15', border: '0.5px solid #7C3AED30' }}>
              Stop
            </div>
          </div>
        )}

        {showMemoTeach && (
          <div style={{ position: 'absolute', bottom: '100%', left: 10, right: 10, marginBottom: 8, padding: '10px 12px', background: '#7C3AED12', border: '0.5px solid #7C3AED30', borderRadius: 10, zIndex: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#7C3AED', marginBottom: 4 }}>Voice Memos</div>
            <div style={{ fontSize: 10, color: theme.textSecondary, lineHeight: 1.5 }}>
              <strong>Tap</strong> the mic to talk to {agentName}. <strong>Hold</strong> the mic to record a voice memo. Tap again to stop. Memos are transcribed and searchable forever.
            </div>
            <div onClick={function() { setShowMemoTeach(false); }} style={{ fontSize: 9, fontWeight: 600, color: '#7C3AED', cursor: 'pointer', marginTop: 6 }}>Got it</div>
          </div>
        )}

        {/* Textarea -- grows */}
        {!voiceMemoMode && <textarea
          ref={function(el) { _textareaRef.current = el; if (inputRef) { if (typeof inputRef === 'function') inputRef(el); else inputRef.current = el; } }}
          data-testid="daily-input"
          value={value || ''}
          onChange={function(e) {
            if (onChange) onChange(e.target.value);
            var el = e.target;
            el.style.height = 'auto';
            el.style.height = Math.min(el.scrollHeight, 120) + 'px';
          }}
          onKeyDown={function(e) {
            if (e.key === 'Enter' && e.altKey) {
              e.preventDefault();
              var el = e.target;
              var start = el.selectionStart;
              var end = el.selectionEnd;
              var current = value || '';
              var newVal = current.substring(0, start) + '\n' + current.substring(end);
              if (onChange) onChange(newVal);
              setTimeout(function() {
                el.selectionStart = el.selectionEnd = start + 1;
                el.style.height = 'auto';
                el.style.height = Math.min(el.scrollHeight, 120) + 'px';
              }, 0);
              return;
            }
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              if (onSend && !sending) onSend();
            }
          }}
          onPaste={onPaste || function() {}}
          placeholder={'Talk to ' + agentName + '...'}
          disabled={false}
          rows={1}
          style={{
            flex: 1, background: 'transparent', border: 'none', outline: 'none', resize: 'none',
            fontSize: 13, color: theme.text, fontFamily: "'IBM Plex Sans', -apple-system, sans-serif",
            lineHeight: 1.5, maxHeight: 120, overflowY: 'auto', padding: '2px 0'
          }}
        />}

        {/* Second mic removed — left mic handles dictation (PD48-VISUAL-QA-S1) */}
        {false && onMicToggle && (
          <div onClick={onMicToggle} style={{ width: 30, height: 30, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: dictating ? (theme.success || '#10B981') : theme.textMuted, flexShrink: 0, position: 'relative' }} title={dictating ? 'Stop dictation' : 'Dictate'}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/><path d="M19 10v2a7 7 0 01-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/>
            </svg>
            {dictating && <div style={{ position: 'absolute', top: 2, right: 2, width: 5, height: 5, borderRadius: '50%', background: theme.success || '#10B981' }} />}
          </div>
        )}

        {/* Paperclip */}
        <svg onClick={function() { if (fileInputRef && fileInputRef.current) fileInputRef.current.click(); }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={theme.textMuted} strokeWidth="1.5" style={{ flexShrink: 0, cursor: 'pointer', marginBottom: 2 }}>
          <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
        </svg>

        {/* Send / Stop */}
        {sending ? (
          <div onClick={function() { if (onStop) onStop(); }} data-testid="daily-stop" style={{ width: 30, height: 30, borderRadius: '50%', background: theme.critical || '#A32D2D', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="#fff"><rect x="6" y="6" width="12" height="12" rx="1"/></svg>
          </div>
        ) : (
          <div onClick={function() { if (onSend) onSend(); }} data-testid="daily-send" style={{ width: 30, height: 30, borderRadius: '50%', background: (value && value.trim()) || (attachments && attachments.length > 0) ? theme.accent : theme.border, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: (value && value.trim()) || (attachments && attachments.length > 0) ? 'pointer' : 'default', transition: 'background 0.2s' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><path d="M12 19V5M5 12l7-7 7 7"/></svg>
          </div>
        )}
      </div>

      {/* Disclaimer */}
      <div style={{ padding: '3px 0', fontSize: 10, color: theme.textMuted, textAlign: 'center' }}>
        Hub can make mistakes. Verify important information.
        {offRecord && <span style={{ color: purple, fontWeight: 500, marginLeft: 4 }}>Off-record -- Hub won't remember this.</span>}
      </div>
    </div>
  );
}
